import React, { useState } from 'react';
import {
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import apiService from '../../services/api';

const SearchPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');
  const [allStatesProgress, setAllStatesProgress] = useState<any>(null);
  const [allStatesRunning, setAllStatesRunning] = useState(false);

  // Category Search Form
  const [categoryForm, setCategoryForm] = useState({
    category: 'Metal Fabrication',
    state: 'MN',
    city: ''
  });

  // Location Search Form
  const [locationForm, setLocationForm] = useState({
    location: 'Minneapolis, MN',
    categories: ['Metal Fabrication']
  });

  // Keyword Search Form
  const [keywordForm, setKeywordForm] = useState({
    keyword: 'CNC machining',
    location: 'Minneapolis, MN'
  });

  // All States Search Form
  const [allStatesForm, setAllStatesForm] = useState({
    categories: ['Metal Fabrication'],
    max_per_state: 50
  });

  const categories = [
    'Metal Fabrication',
    'Welding Services',
    'Agricultural Equipment',
    'Auto Repair',
    'Machine Shop'
  ];

  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  const handleCategorySearch = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiService.searchByCategory({
        ...categoryForm,
        save_results: true
      });
      setResults(response);
    } catch (err: any) {
      setError(err.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLocationSearch = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiService.searchByLocation({
        ...locationForm,
        save_results: true
      });
      setResults(response);
    } catch (err: any) {
      setError(err.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleKeywordSearch = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiService.searchByKeyword({
        ...keywordForm,
        save_results: true
      });
      setResults(response);
    } catch (err: any) {
      setError(err.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAllStatesSearch = async () => {
    setAllStatesRunning(true);
    setError('');
    setAllStatesProgress(null);
    setResults(null);

    try {
      // Start the all-states search
      const response = await fetch('/api/search/all-states', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(allStatesForm),
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Unable to read response');
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let finalResults: any = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.trim()) {
            try {
              const data = JSON.parse(line);
              if (data.type === 'progress' || data.type === 'state_started' || data.type === 'state_completed') {
                setAllStatesProgress(data);
              } else if (data.type === 'final_results') {
                finalResults = data;
              }
            } catch (e) {
              console.warn('Failed to parse progress line:', line);
            }
          }
        }
      }

      if (finalResults) {
        setResults(finalResults);
      }
    } catch (err: any) {
      setError(err.message || 'All-states search failed');
    } finally {
      setAllStatesRunning(false);
      setAllStatesProgress(null);
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Lead Discovery
      </Typography>

      <Grid container spacing={3}>
        {/* Category Search */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Search by Category
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Business Category</InputLabel>
                    <Select
                      value={categoryForm.category}
                      onChange={(e) => setCategoryForm({...categoryForm, category: e.target.value})}
                      label="Business Category"
                    >
                      {categories.map((cat) => (
                        <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>State</InputLabel>
                    <Select
                      value={categoryForm.state}
                      onChange={(e) => setCategoryForm({...categoryForm, state: e.target.value})}
                      label="State"
                    >
                      {states.map((state) => (
                        <MenuItem key={state} value={state}>{state}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="City (Optional)"
                    value={categoryForm.city}
                    onChange={(e) => setCategoryForm({...categoryForm, city: e.target.value})}
                    placeholder="e.g., Minneapolis"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={handleCategorySearch}
                    disabled={loading || allStatesRunning}
                    fullWidth
                  >
                    {loading ? <CircularProgress size={24} /> : 'Search by Category'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Location Search */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Search by Location
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Location"
                    value={locationForm.location}
                    onChange={(e) => setLocationForm({...locationForm, location: e.target.value})}
                    placeholder="e.g., Minneapolis, MN"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={handleLocationSearch}
                    disabled={loading || allStatesRunning}
                    fullWidth
                  >
                    {loading ? <CircularProgress size={24} /> : 'Search by Location'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Keyword Search */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Search by Keyword
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Keyword/Phrase"
                    value={keywordForm.keyword}
                    onChange={(e) => setKeywordForm({...keywordForm, keyword: e.target.value})}
                    placeholder="e.g., CNC machining, plasma cutting"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Location"
                    value={keywordForm.location}
                    onChange={(e) => setKeywordForm({...keywordForm, location: e.target.value})}
                    placeholder="e.g., Minneapolis, MN"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={handleKeywordSearch}
                    disabled={loading || allStatesRunning}
                    fullWidth
                  >
                    {loading ? <CircularProgress size={24} /> : 'Search by Keyword'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* All States Search */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Search All 50 States
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Business Categories</InputLabel>
                    <Select
                      multiple
                      value={allStatesForm.categories}
                      onChange={(e) => setAllStatesForm({...allStatesForm, categories: e.target.value as string[]})}
                      label="Business Categories"
                    >
                      {categories.map((cat) => (
                        <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Leads per State"
                    value={allStatesForm.max_per_state}
                    onChange={(e) => setAllStatesForm({...allStatesForm, max_per_state: parseInt(e.target.value) || 50})}
                    inputProps={{ min: 10, max: 100 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleAllStatesSearch}
                    disabled={loading || allStatesRunning}
                    fullWidth
                  >
                    {allStatesRunning ? <CircularProgress size={24} /> : 'Search All States'}
                  </Button>
                </Grid>

                {allStatesProgress && (
                  <Grid item xs={12}>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        {allStatesProgress.type === 'state_started' && `Processing ${allStatesProgress.state}... (${allStatesProgress.progress})`}
                        {allStatesProgress.type === 'state_completed' && `Completed ${allStatesProgress.state}: ${allStatesProgress.found} leads found`}
                        {allStatesProgress.type === 'progress' && `Overall Progress: ${allStatesProgress.completed}/${allStatesProgress.total} states`}
                      </Typography>
                      {allStatesProgress.type === 'progress' && (
                        <LinearProgress
                          variant="determinate"
                          value={(allStatesProgress.completed / allStatesProgress.total) * 100}
                          sx={{ mt: 1 }}
                        />
                      )}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {/* Results Display */}
      {results && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Search Results
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="textSecondary">Total Found</Typography>
                <Typography variant="h6">{results.total_found}</Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="textSecondary">New Leads Saved</Typography>
                <Typography variant="h6" color="primary">{results.saved_count}</Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="textSecondary">Duplicates Skipped</Typography>
                <Typography variant="h6">{results.duplicate_count}</Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="textSecondary">Errors</Typography>
                <Typography variant="h6">{results.error_count}</Typography>
              </Grid>
            </Grid>

            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                href="/leads"
                onClick={() => window.location.href = '/leads'}
              >
                View All Leads
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default SearchPage;